import type { MessageType } from '@talk2nick/core';

/**
 * 미디어 클라이언트 전처리 (PLAN 5-5장).
 *
 * 업로드 전에 용량과 화질을 줄인다 — 모바일 데이터 과금과 Storage 비용을
 * 막는 것이 목적이라, 변환이 불가능한 포맷은 여기서 실패시킨다.
 *
 *  - 사진: 1600px 리사이즈 + WebP q0.8 (Safari가 WebP 인코딩을 못 하면 JPEG로 폴백)
 *  - 영상: 원본 유지(트랜스코딩은 WebCodecs worker가 필요해 후속), 첫 프레임 썸네일
 *  - 음성: 60초 자동 종료, 인코딩은 브라우저가 결정
 *  - 파일: 50MB 상한
 */

/** PLAN 5-5장 제약값 */
export const MAX_IMAGE_EDGE = 1_600;
export const MAX_THUMB_EDGE = 200;
export const IMAGE_QUALITY = 0.8;
export const MAX_VIDEO_BYTES = 200 * 1024 * 1024;
export const MAX_FILE_BYTES = 50 * 1024 * 1024;
export const MAX_AUDIO_SECONDS = 60;

export type MediaKind = Extract<MessageType, 'image' | 'video' | 'audio' | 'file'>;

/** 실패 사유 코드 — UI가 이 값으로 i18n 문구를 고른다 */
export type MediaErrorCode = 'too-large' | 'unsupported' | 'prepare-failed' | 'mic-denied';

export class MediaError extends Error {
  constructor(
    readonly code: MediaErrorCode,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'MediaError';
  }
}

/** 전처리를 마친 미디어 — Firestore에 들어갈 값과 업로드할 Blob을 함께 담는다 */
export interface PreparedMedia {
  kind: MediaKind;
  /** 실제 업로드할 데이터 (이미지는 WebP로 재인코딩된 결과) */
  blob: Blob;
  mime: string;
  size: number;
  fileName?: string;
  width?: number;
  height?: number;
  durationMs?: number;
  /** 목록/썸네일용 200px WebP — 원본 없이 미리보기를 그리기 위해 별도 업로드 */
  thumbnail?: { blob: Blob; width: number; height: number };
}

export function kindOf(input: { type: string }): MediaKind {
  if (input.type.startsWith('image/')) return 'image';
  if (input.type.startsWith('video/')) return 'video';
  if (input.type.startsWith('audio/')) return 'audio';
  return 'file';
}

/** 목록 미리보기용 이모지 — 언어와 무관하게 종류를 알린다 */
export function mediaGlyph(kind: MediaKind): string {
  switch (kind) {
    case 'image':
      return '📷';
    case 'video':
      return '🎬';
    case 'audio':
      return '🎤';
    case 'file':
      return '📎';
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/** Storage 경로 확장자 */
export function extensionFor(mime: string, kind: MediaKind): string {
  const known: Record<string, string> = {
    'image/webp': 'webp',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/heic': 'heic',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/quicktime': 'mov',
    'audio/webm': 'webm',
    'audio/mp4': 'm4a',
    'audio/ogg': 'ogg',
    'audio/mpeg': 'mp3',
  };
  return known[mime] ?? kind;
}

function fitSize(width: number, height: number, maxEdge: number) {
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function draw(source: CanvasImageSource, width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new MediaError('prepare-failed', 'canvas-unavailable');
  ctx.drawImage(source, 0, 0, width, height);
  return canvas;
}

async function toBlob(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob> {
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mime, quality));
  if (!blob) throw new MediaError('prepare-failed', 'encode-failed');
  return blob;
}

let webpSupport: boolean | null = null;

/** Safari 14 미만 등은 WebP 인코딩이 안 된다 — JPEG로 폴백한다 */
function imageMime(): string {
  if (webpSupport === null) {
    const probe = document.createElement('canvas');
    probe.width = 1;
    probe.height = 1;
    webpSupport = probe.toDataURL('image/webp').startsWith('data:image/webp');
  }
  return webpSupport ? 'image/webp' : 'image/jpeg';
}

function waitForEvent(el: HTMLMediaElement, event: string, timeoutMs = 8_000): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      cleanup();
      reject(new MediaError('prepare-failed', `timeout:${event}`));
    }, timeoutMs);
    const onOk = () => {
      cleanup();
      resolve();
    };
    const onErr = () => {
      cleanup();
      reject(new MediaError('unsupported', 'decode-failed'));
    };
    const cleanup = () => {
      window.clearTimeout(timer);
      el.removeEventListener(event, onOk);
      el.removeEventListener('error', onErr);
    };
    el.addEventListener(event, onOk, { once: true });
    el.addEventListener('error', onErr, { once: true });
  });
}

/** 사진: 1600px + 썸네일 200px (PLAN 5-5장) */
export async function prepareImage(input: File | Blob, fileName?: string): Promise<PreparedMedia> {
  let bitmap: ImageBitmap;
  try {
    // Safari는 HEIC도 createImageBitmap로 디코딩한다 — 서버 변환이 필요 없다
    bitmap = await createImageBitmap(input);
  } catch (error) {
    throw new MediaError('unsupported', `image-decode:${String(error)}`);
  }

  try {
    const mime = imageMime();
    const main = fitSize(bitmap.width, bitmap.height, MAX_IMAGE_EDGE);
    const blob = await toBlob(draw(bitmap, main.width, main.height), mime, IMAGE_QUALITY);

    const thumb = fitSize(bitmap.width, bitmap.height, MAX_THUMB_EDGE);
    const thumbnail = {
      blob: await toBlob(draw(bitmap, thumb.width, thumb.height), mime, IMAGE_QUALITY),
      width: thumb.width,
      height: thumb.height,
    };

    return {
      kind: 'image',
      blob,
      mime,
      size: blob.size,
      fileName,
      width: main.width,
      height: main.height,
      thumbnail,
    };
  } finally {
    bitmap.close();
  }
}

/** 영상: 원본을 올리고 첫 프레임을 썸네일로 잡는다 (PLAN 5-5장 "720p transcoding 또는 원본") */
export async function prepareVideo(file: File): Promise<PreparedMedia> {
  if (file.size > MAX_VIDEO_BYTES) throw new MediaError('too-large', 'video');

  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true;
  video.playsInline = true;
  video.src = url;

  try {
    await waitForEvent(video, 'loadeddata');
    const sourceWidth = video.videoWidth || MAX_THUMB_EDGE;
    const sourceHeight = video.videoHeight || MAX_THUMB_EDGE;
    const mime = imageMime();
    const thumb = fitSize(sourceWidth, sourceHeight, MAX_THUMB_EDGE);

    return {
      kind: 'video',
      blob: file,
      mime: file.type || 'video/mp4',
      size: file.size,
      fileName: file.name,
      width: sourceWidth,
      height: sourceHeight,
      durationMs: Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : undefined,
      thumbnail: {
        blob: await toBlob(draw(video, thumb.width, thumb.height), mime, IMAGE_QUALITY),
        width: thumb.width,
        height: thumb.height,
      },
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** 음성: 길이 상한만 확인한다 (PLAN 5-5장 60초 자동 종료는 녹음기에서 처리) */
export function prepareAudio(blob: Blob, durationMs: number, fileName?: string): PreparedMedia {
  if (durationMs > MAX_AUDIO_SECONDS * 1000) throw new MediaError('too-large', 'audio');
  return {
    kind: 'audio',
    blob,
    mime: blob.type || 'audio/webm',
    size: blob.size,
    fileName,
    durationMs,
  };
}

/** 일반 파일: 그대로 */
export function prepareFile(file: File): PreparedMedia {
  if (file.size > MAX_FILE_BYTES) throw new MediaError('too-large', 'file');
  return {
    kind: 'file',
    blob: file,
    mime: file.type || 'application/octet-stream',
    size: file.size,
    fileName: file.name,
  };
}

/** 파일 1개를 종류에 맞는 전처리 경로로 보낸다 */
export async function prepareMedia(file: File): Promise<PreparedMedia> {
  switch (kindOf(file)) {
    case 'image':
      return prepareImage(file, file.name);
    case 'video':
      return prepareVideo(file);
    default:
      return prepareFile(file);
  }
}
